import { useEffect, useState } from "react";
import { ArrowLeft, ArrowRight, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { useApp } from "@/context/AppContext";
import { api, errMsg } from "@/lib/api";
import Disclaimer from "@/components/journey/Disclaimer";
import SlotPicker from "@/components/journey/SlotPicker";
import PaymentPanel from "@/components/journey/PaymentPanel";
import { isFormValid } from "@/components/journey/StepDetails";

function EmailText({ text, email }) {
  const [a, b] = text.split("{email}");
  return (
    <>
      {a}
      <a href={`mailto:${email}`} data-testid="support-email-link" className="text-amber-200 underline underline-offset-4">{email}</a>
      {b}
    </>
  );
}

function SelectedAmount() {
  const { settings, state, t, pick } = useApp();
  const svc = settings.services.find((s) => s.id === state.form.service_id);
  if (!svc) return null;
  return (
    <div className="flex items-baseline justify-between gap-4 rounded-2xl border border-emerald-300/20 bg-black/15 p-4 text-sm" data-testid="selected-amount">
      <span className="text-emerald-100/80">{pick(svc, "label")}</span>
      <span className="font-display text-xl text-amber-200">Rs {svc.price}</span>
    </div>
  );
}

export default function StepBooking() {
  const { settings, state, update, t, pick, lang } = useApp();
  const [date, setDate] = useState(state.booking?.date || "");
  const [time, setTime] = useState("");
  const [busy, setBusy] = useState(false);
  const booking = state.booking?.status === "awaiting_payment" ? state.booking : null;
  const tx = settings.texts;

  useEffect(() => {
    if (!isFormValid(state.form)) update({ step: 2 });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const reserve = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/bookings", { ...state.form, age: Number(state.form.age), lang, date, time });
      update({ booking: data });
    } catch (e) {
      toast.error(errMsg(e));
      setTime("");
    } finally {
      setBusy(false);
    }
  };

  const changeSlot = async () => {
    await api.post(`/bookings/${booking.id}/cancel-hold`, { token: booking.token }).catch(() => {});
    update({ booking: null });
    setTime("");
  };

  const paid = (data) => update({ booking: { ...state.booking, ...data }, step: 4 });

  return (
    <section className="glass space-y-6 rounded-[2rem] p-6 sm:p-10" data-testid="step-booking">
      <h2 className="font-display text-2xl text-white">{t("bookingTitle")}</h2>
      <Disclaimer
        testId="booking-disclaimer"
        items={[pick(tx, "p3_disclaimer_1"), <EmailText key="e" text={pick(tx, "p3_disclaimer_2")} email={settings.contact_email} />]}
      />
      <SelectedAmount />
      {!booking ? (
        <>
          <div className="space-y-3">
            <p className="font-display text-lg text-white">{t("chooseDate")}</p>
            <SlotPicker date={date} time={time} onDate={(d) => { setDate(d); setTime(""); }} onTime={setTime} />
          </div>
          <div className="flex items-center justify-between">
            <Button variant="ghost" data-testid="booking-back-btn" onClick={() => update({ step: 2 })} className="rounded-full text-emerald-100 hover:bg-white/10">
              <ArrowLeft className="mr-2 h-4 w-4" /> {t("back")}
            </Button>
            <Button data-testid="proceed-to-payment-btn" disabled={!date || !time || busy} onClick={reserve} className="h-12 rounded-full bg-emerald-400 px-8 text-emerald-950 hover:bg-emerald-300">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <>{t("proceedPayment")} <ArrowRight className="ml-2 h-4 w-4" /></>}
            </Button>
          </div>
        </>
      ) : (
        <div className="space-y-4">
          <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-emerald-300/20 bg-black/20 p-4" data-testid="held-slot-summary">
            <div className="text-sm text-emerald-50">
              <span className="text-emerald-100/60">{t("date")}:</span> {booking.date} · <span className="text-emerald-100/60">{t("time")}:</span> {booking.time} ·{" "}
              <span className="text-amber-200">Rs {booking.amount}</span>
              <p className="mt-1 text-xs text-emerald-100/60">{t("slotHeld")}</p>
            </div>
            <Button variant="ghost" size="sm" data-testid="change-slot-btn" onClick={changeSlot} className="rounded-full text-emerald-100 hover:bg-white/10">
              {t("changeSlot")}
            </Button>
          </div>
          <PaymentPanel booking={booking} onDone={paid} allowSkip={settings?.allow_skip_payment !== false} />
        </div>
      )}
    </section>
  );
}
